import { useState, useEffect, useRef } from 'react';
import { parseChainTime } from '../utils/chainTime';

// get_block → { num, producer, timestamp (Date), trxCount }
export const normalizeBlock = (b, num) => ({
  num: b.block_num ?? num,
  producer: b.producer,
  timestamp: parseChainTime(b.timestamp),
  trxCount: Array.isArray(b.transactions) ? b.transactions.length : 0
});

// Pide los bloques `nums` de `concurrency` en `concurrency`; los que fallan se omiten
export const fetchBlocks = async (blockchainService, nums, concurrency = 2, cache = null) => {
  const result = new Array(nums.length);
  let next = 0;
  const worker = async () => {
    while (next < nums.length) {
      const i = next++;
      const num = nums[i];
      try {
        if (cache && cache.has(num)) { result[i] = cache.get(num); continue; }
        const block = normalizeBlock(await blockchainService.getBlock(num), num);
        if (cache) cache.set(num, block);
        result[i] = block;
      } catch { result[i] = null; }
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, nums.length) }, worker));
  return result.filter(Boolean);
};

/**
 * Últimos `count` bloques de la cadena, refrescados cada `intervalMs`.
 * Cada refresco = 1 get_info + `count` get_block (de 2 en 2, para no saturar
 * la API). No pide nada con la pestaña oculta.
 *
 * Con `paused` deja de refrescar (se conserva la última lista).
 *
 * Devuelve { blocks: [{ num, producer, timestamp (Date), trxCount }], loading, error }
 */
const useLatestBlocks = (blockchainService, { count = 6, intervalMs = 12000, paused = false } = {}) => {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Bloques ya descargados (son inmutables), para no volver a pedirlos
  const cacheRef = useRef(new Map());
  // En ref para que pausar/reanudar no reinicie la lista
  const pausedRef = useRef(paused);
  const refreshRef = useRef(null);

  useEffect(() => {
    pausedRef.current = paused;
    if (!paused && refreshRef.current) refreshRef.current(); // al reanudar, refrescar ya
  }, [paused]);

  useEffect(() => {
    if (!blockchainService) return undefined;
    let cancelled = false;
    let running = false;
    cacheRef.current = new Map();
    setBlocks([]);
    setLoading(true);
    setError(null);

    const refresh = async () => {
      if (running || pausedRef.current || (typeof document !== 'undefined' && document.hidden)) return;
      running = true;
      try {
        const info = await blockchainService.getInfo();
        const head = info.head_block_num;
        const nums = Array.from({ length: count }, (_, i) => head - i);
        const ok = await fetchBlocks(blockchainService, nums, 2, cacheRef.current);
        if (cancelled) return;
        // Podar la cache a los bloques visibles
        const keep = new Set(nums);
        for (const k of cacheRef.current.keys()) if (!keep.has(k)) cacheRef.current.delete(k);
        if (ok.length) {
          setBlocks(ok);
          setError(null);
        } else {
          setError('Could not load the latest blocks');
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load the latest blocks');
      } finally {
        running = false;
        if (!cancelled) setLoading(false);
      }
    };

    refreshRef.current = refresh;
    refresh();
    const id = setInterval(refresh, intervalMs);
    return () => { cancelled = true; clearInterval(id); refreshRef.current = null; };
  }, [blockchainService, count, intervalMs]);

  return { blocks, loading, error };
};

// "3 secs ago", "2 mins ago"…
export const timeAgo = (date, now = Date.now()) => {
  const s = Math.max(0, Math.round((now - date.getTime()) / 1000));
  if (s < 60) return `${s} sec${s === 1 ? '' : 's'} ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min${m === 1 ? '' : 's'} ago`;
  const h = Math.round(m / 60);
  return `${h} hr${h === 1 ? '' : 's'} ago`;
};

export default useLatestBlocks;
